import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  EntryStep,
  EntryStepCode,
  EntryStepName,
  EntryStepOutcome,
  PreflightDevice,
} from "@edu/shared";
import { sendEntryTelemetry } from "../../lib/api.ts";
import {
  allShownSettled,
  entryView,
  SHOWN_STEPS,
  type EntryView,
  type ShownStep,
  type StepState,
} from "./entry-steps.ts";

/**
 * Учёт шагов входа в урок: что уже сделано, давно ли было движение, и что из
 * этого показывать (раздел 4.4 `docs/room-preflight-design.md`).
 *
 * ⚠️ Хук живёт в `LessonPage`, то есть ВЫШЕ комнаты, а показывает его результат
 * два разных места: сама страница (шаги «доступ» и «чанк комнаты», пока комнаты
 * ещё нет) и оверлей ВНУТРИ комнаты (шаг «доска»). Иначе никак: состояние доски
 * знает только `Whiteboard`, а он монтируется после того, как чанк приехал, то
 * есть экран, стоящий выше, про этот шаг не узнал бы никогда.
 *
 * ⚠️ Ни одного абсолютного таймера здесь нет. Единственное время, которое
 * считается, это «сколько прошло с последнего движения», и движением считается
 * либо смена состояния шага, либо доехавшие байты (см. `useProgressClock`).
 */
export interface EntryTracker {
  view: EntryView;
  /** Состояния показываемых шагов: по ним вызывающие решают, что рисовать. */
  shown: Readonly<Record<ShownStep, StepState>>;
  /** Шаг начался. Повторный вызов ничего не делает: `StrictMode` зовёт эффекты дважды. */
  begin: (step: EntryStepName) => void;
  /** Шаг кончился. Повторный вызов ничего не делает: первый исход честнее второго. */
  mark: (step: EntryStepName, outcome: EntryStepOutcome, code?: EntryStepCode) => void;
}

interface Rec {
  startedAt: number | null;
  outcome: EntryStepOutcome | null;
  ms: number;
  code: EntryStepCode | null;
}

const ALL_STEPS: readonly EntryStepName[] = [...SHOWN_STEPS, "audio"];

function emptyRecords(): Record<EntryStepName, Rec> {
  return {
    access: { startedAt: null, outcome: null, ms: 0, code: null },
    "room-chunk": { startedAt: null, outcome: null, ms: 0, code: null },
    board: { startedAt: null, outcome: null, ms: 0, code: null },
    audio: { startedAt: null, outcome: null, ms: 0, code: null },
  };
}

function stateOf(rec: Rec): StepState {
  if (rec.outcome === null) return rec.startedAt === null ? "wait" : "run";
  return rec.outcome === "ok" ? "ok" : "fail";
}

export function useEntryTracker(): EntryTracker {
  const [recs, setRecs] = useState<Record<EntryStepName, Rec>>(emptyRecords);
  const progress = useProgressClock();
  const bump = progress.bump;

  const begin = useCallback(
    (step: EntryStepName) => {
      bump();
      setRecs((prev) =>
        prev[step].startedAt === null
          ? { ...prev, [step]: { ...prev[step], startedAt: Date.now() } }
          : prev,
      );
    },
    [bump],
  );

  const mark = useCallback(
    (step: EntryStepName, outcome: EntryStepOutcome, code?: EntryStepCode) => {
      bump();
      setRecs((prev) => {
        const rec = prev[step];
        if (rec.outcome !== null) return prev;
        const startedAt = rec.startedAt ?? Date.now();
        return {
          ...prev,
          [step]: {
            startedAt,
            outcome,
            ms: Math.max(0, Date.now() - startedAt),
            code: code ?? null,
          },
        };
      });
    },
    [bump],
  );

  const shown = useMemo(
    () => ({
      access: stateOf(recs.access),
      "room-chunk": stateOf(recs["room-chunk"]),
      board: stateOf(recs.board),
    }),
    [recs],
  );

  const settled = allShownSettled(shown);

  /**
   * Часы, по которым пересчитываются пороги.
   *
   * ⚠️ Отдельное состояние `now` здесь обязательно: молчание меряется часами, а
   * не состоянием шагов, и без тика подпись «долго» не появилась бы никогда,
   * потому что перерисовывать компонент в эти восемь секунд некому.
   *
   * Секунда это не частота обновления экрана, а разрешение порогов: обе цифры
   * (восемь и двадцать) круглые, и промахнуться на секунду не жалко. Тикать
   * после того, как всё разрешилось, незачем: интервал снимается.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (settled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [settled]);

  const view = entryView({
    steps: shown,
    msSinceProgress: Math.max(0, now - progress.at()),
  });

  useSendOnce(settled, recs);

  return { view, shown, begin, mark };
}

/**
 * Отправка события входа, ровно один раз за монтирование страницы.
 *
 * ⚠️ Событие уходит, когда разрешились ПОКАЗЫВАЕМЫЕ шаги, а не все четыре. Шаг
 * `audio` попадает в него, если к этой секунде успел разрешиться, и не попадает
 * иначе. Ждать его нельзя: он не завершается вовсе, когда соединение молча
 * висит, а это ровно тот вход, ради которого прибор и заведён. Событие без
 * `audio` читается честно («звук к моменту готовности доски ещё не поднялся»),
 * а неотправленное событие не читается никак.
 *
 * Отказ отправки глушится целиком: телеметрия входа не имеет права быть видна
 * на входе.
 */
function useSendOnce(settled: boolean, recs: Record<EntryStepName, Rec>): void {
  const sent = useRef(false);
  const latest = useRef(recs);
  useEffect(() => {
    latest.current = recs;
  });

  useEffect(() => {
    if (!settled || sent.current) return;
    sent.current = true;
    const steps: EntryStep[] = [];
    for (const name of ALL_STEPS) {
      const rec = latest.current[name];
      if (rec.outcome === null) continue;
      steps.push({
        step: name,
        ms: Math.min(rec.ms, 3_600_000),
        outcome: rec.outcome,
        ...(rec.code === null ? {} : { code: rec.code }),
      });
    }
    const device = entryDevice();
    void sendEntryTelemetry({
      event: "room_entry",
      steps,
      ...(device === null ? {} : { device }),
    }).catch(() => undefined);
  }, [settled]);
}

/**
 * Часы молчания.
 *
 * Что считается движением, кроме смены состояния шага: доехавшие байты. Их
 * отдаёт Resource Timing, и отдаёт он их **по завершении файла**, а не по ходу
 * скачивания. Отсюда прямое следствие, записанное в `STALLABLE`: у шага, где
 * на проводе один большой файл, эти часы не доказывают ничего, и кнопки
 * перезагрузки такой шаг не получает.
 *
 * ⚠️ `PerformanceObserver` может отсутствовать (старый браузер, тест). Тогда
 * движением остаются только шаги, и это ровно то поведение, которое было бы без
 * наблюдателя: не хуже, просто грубее.
 */
function useProgressClock(): { bump: () => void; at: () => number } {
  const at = useRef(Date.now());
  const bump = useCallback(() => {
    at.current = Date.now();
  }, []);
  const read = useCallback(() => at.current, []);

  useEffect(() => {
    if (typeof PerformanceObserver === "undefined") return;
    const obs = new PerformanceObserver((list) => {
      let bytes = 0;
      for (const e of list.getEntries()) {
        const r = e as PerformanceResourceTiming;
        bytes += r.encodedBodySize > 0 ? r.encodedBodySize : r.transferSize;
      }
      if (bytes > 0) at.current = Date.now();
    });
    try {
      obs.observe({ type: "resource", buffered: false });
    } catch {
      // Тип наблюдения бывает неизвестен движку. Тогда движением остаются шаги
      return;
    }
    return () => obs.disconnect();
  }, []);

  return { bump, at: read };
}

/**
 * Обстановка для телеметрии входа.
 *
 * ⚠️ Экспортируется наружу ради экрана предполётной проверки
 * (`features/preflight/PreflightPage.tsx`): контракт события у него другой
 * (`preflight_done` вместо `room_entry`), а обстановка ровно та же, и вторая
 * копия этих двадцати строк разъехалась бы с первой молча.
 *
 * ⚠️ Своя сборка, а не `snapshot()` из `features/feedback/context.ts`, хотя
 * данные те же. Причина в контракте: у снимка заметки `effectiveType` это
 * свободная строка, а у `PreflightDevice` перечисление из четырёх значений, и
 * размеры окна там лежат вложенным объектом, а здесь плоско. Переиспользование
 * означало бы перекладывание с проверкой каждого поля, то есть тот же объём кода
 * плюс молчаливый риск отдать схеме пятое значение и потерять всё событие.
 */
const EFFECTIVE_TYPES: readonly string[] = ["slow-2g", "2g", "3g", "4g"];

export function entryDevice(): PreflightDevice | null {
  if (typeof window === "undefined") return null;
  const info = (navigator as { connection?: { effectiveType?: unknown; downlink?: unknown } })
    .connection;
  const effectiveType =
    typeof info?.effectiveType === "string" && EFFECTIVE_TYPES.includes(info.effectiveType)
      ? (info.effectiveType as "slow-2g" | "2g" | "3g" | "4g")
      : null;
  const downlink =
    typeof info?.downlink === "number" && Number.isFinite(info.downlink) && info.downlink >= 0
      ? info.downlink
      : null;
  return {
    w: Math.round(window.innerWidth),
    h: Math.round(window.innerHeight),
    dpr: Math.min(16, window.devicePixelRatio),
    effectiveType,
    downlink,
  };
}
